import { drugUnitValue, roundQuantity } from '@/composables/inventory/inventoryTable';
import type { MedicineBulkPurchasePayload, MedicineBulkPurchaseRow } from '@/types';

/** What the user typed into one row; packs and pack size are raw input (a number input may hand over a number or a string). */
export type BulkPurchaseEntry = {
    /** Ticked: the drug was bought and the row is saved. */
    bought: boolean;
    packs: string | number;
    packSize: string | number;
    batchNumber: string;
    note: string;
    /** The packs and pack size the row started from: the last purchase on or before the date it was seeded for. */
    readonly seed: { packs: string | number; packSize: string | number };
};

/**
 * A row starts unticked, with the packs and pack size the drug was last bought in on or before the chosen date;
 * a drug never bought starts empty. An untouched row is seeded again when the date changes, so it always
 * starts from the purchase its row names.
 */
export function entryFor(row: MedicineBulkPurchaseRow): BulkPurchaseEntry {
    const seed = { packs: row.last_purchase?.packs ?? '', packSize: row.last_purchase?.pack_size ?? '' };

    return { bought: false, packs: seed.packs, packSize: seed.packSize, batchNumber: '', note: '', seed };
}

/** Whether a row was ticked or changed from the seed it started from, which a change of farm or closing the dialog would lose. */
export function isBulkPurchaseEntryDirty(entry: BulkPurchaseEntry): boolean {
    return entry.bought || entry.batchNumber.trim() !== '' || entry.note.trim() !== '' || String(entry.packs) !== String(entry.seed.packs) || String(entry.packSize) !== String(entry.seed.packSize);
}

export type BulkPurchaseRowProblem = 'invalidPacks' | 'invalidPackSize';

export type BulkPurchaseRowState = {
    /** Not ticked: the row is greyed out and not saved. */
    skipped: boolean;
    packs: number | null;
    packSize: number | null;
    /** Packs times pack size, once both are valid. */
    total: number | null;
    problems: BulkPurchaseRowProblem[];
};

/**
 * Where one row stands: skipped when not ticked, otherwise the figures it would save and what stops it from
 * being saved - a whole number of packs above zero and a pack size above zero, as the server requires. The
 * server also caps both; a figure past its column is refused on save and shown under the row.
 */
export function evaluateBulkPurchaseRow(entry: BulkPurchaseEntry): BulkPurchaseRowState {
    if (!entry.bought) {
        return { skipped: true, packs: null, packSize: null, total: null, problems: [] };
    }

    const packsRaw = String(entry.packs ?? '').trim();
    const packSizeRaw = String(entry.packSize ?? '').trim();
    const packs = packsRaw === '' ? NaN : Number(packsRaw);
    const packSize = packSizeRaw === '' ? NaN : Number(packSizeRaw);
    const problems: BulkPurchaseRowProblem[] = [];

    if (!Number.isInteger(packs) || packs <= 0) {
        problems.push('invalidPacks');
    }
    if (!Number.isFinite(packSize) || packSize <= 0) {
        problems.push('invalidPackSize');
    }
    if (problems.length > 0) {
        return { skipped: false, packs: null, packSize: null, total: null, problems };
    }

    return { skipped: false, packs, packSize, total: roundQuantity(packs * roundQuantity(packSize)), problems };
}

/** The request body for every ticked row; unticked rows are left out. */
export function bulkPurchasePayload(farmId: number, date: string, rows: MedicineBulkPurchaseRow[], entries: Record<string, BulkPurchaseEntry>): MedicineBulkPurchasePayload {
    const trimmedOrNull = (value: string) => (value.trim() === '' ? null : value.trim());

    return {
        farm_id: farmId,
        date,
        rows: rows.flatMap((row) => {
            const entry = entries[drugUnitValue(row)];

            if (!entry) {
                return [];
            }

            const { skipped, packs, packSize } = evaluateBulkPurchaseRow(entry);

            return skipped || packs === null || packSize === null
                ? []
                : [{ drug_id: row.drug_id, dosage_unit_id: row.unit_id, packs, pack_size: packSize, batch_number: trimmedOrNull(entry.batchNumber), note: trimmedOrNull(entry.note) }];
        }),
    };
}
